<template>
	<div>
		<h2 class="heading-4">About {{ props.asset.name }}</h2>
		<div class="asset-details-about-company">
			<div v-if="displayPrimaryMediaItem" class="col-5-md col-5-lg">
				<video
					v-if="isVideoPrimaryItem"
					data-test="primary-item-video"
					:poster="primaryItemImage.url"
					controls
					allow="fullscreen"
					crossorigin="Anonymous"
					width="100%"
					playsinline="true"
					class="asset-details-about-company__primary-item cursor-pointer"
					><source :src="primaryItemVideo" type="video/mp4" />
				</video>
				<div v-else-if="isImagePrimaryItem" role="figure" data-test="primary-item-image">
					<div
						v-if="overlayLink"
						class="asset-details-about-company__image-with-link"
						data-test="primary-item-overlay-link"
					>
						<img
							v-reveal:fade-up
							:src="primaryItemImage.url"
							:alt="primaryItemImage.altText"
							role="presentation"
							class="asset-details-about-company__primary-item"
						/>
						<BaseLink classes="asset-details-about-company__overlay-link" :link="overlayLink">
							<img
								v-if="overlayLink.icon"
								:src="overlayLink.icon.url"
								:alt="overlayLink.icon.altText"
								role="presentation"
								style="height: 16px; width: 16px"
							/><span class="body-sm">{{ overlayLink.text }}</span>
						</BaseLink>
					</div>
					<MediaGalleryPreview
						v-else-if="mediaGallery"
						data-test="primary-item-media-gallery"
						:media-items="mediaGallery"
					/>
				</div>
				<div v-if="secondaryMediaItems" class="asset-details-about-company__secondary-items">
					<AssetDetailsMediaCard v-for="(item, itemIdx) in secondaryMediaItems" :key="itemIdx" :item="item" />
				</div>
			</div>

			<div class="col-5-md col-7-lg">
				<h3 v-if="aboutAssetSectionTitle" class="font-weight-bold mb-50">{{ aboutAssetSectionTitle }}</h3>
				<RichTextComponent :blocks="aboutAssetSectionContent" />
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { Asset, AssetSecondaryMediaItem } from 'types/assets';
import AssetDetailsMediaCard from '@components/marketing-site/asset-details/asset-details-media-card.vue';
import { computed } from 'vue';
import { InvestorUpdateMediaItem } from 'types/account';
import { Link } from 'types/layout';
import MediaGalleryPreview from '@components/account/media-gallery-preview.vue';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import { SanityRichTextBlock } from 'types/sanity';

interface PrimaryMediaOverlayLink extends Link {
	icon?: {
		url: string;
		altText: string;
	} | null;
}

interface Props {
	asset: Asset;
}

const props = defineProps<Props>();

const secondaryMediaItems = computed(
	(): Array<AssetSecondaryMediaItem> | undefined => props.asset.ventureCapitalFields?.secondaryMediaItems
);

const isVideoPrimaryItem = computed(
	(): boolean => props.asset.ventureCapitalFields?.primaryMediaItem?.mediaContentType === 'VIDEO'
);

const isImagePrimaryItem = computed(
	(): boolean => props.asset.ventureCapitalFields?.primaryMediaItem?.mediaContentType === 'IMAGE'
);

const displayPrimaryMediaItem = computed((): boolean => isVideoPrimaryItem.value || isImagePrimaryItem.value);

const primaryItemImage = computed((): { url: string; altText: string } => ({
	url: props.asset.ventureCapitalFields?.primaryMediaItem?.mediaItemImage?.url ?? '',
	altText: 'Primary image'
}));

const primaryItemVideo = computed(
	(): string => props.asset.ventureCapitalFields?.primaryMediaItem?.mediaItemVideo?.url ?? ''
);

const overlayLink = computed((): PrimaryMediaOverlayLink | null => {
	const link = props.asset.ventureCapitalFields?.primaryMediaItem?.mediaOverlayLink;

	return link?.url
		? {
				icon: link.icon
					? {
							url: link.icon?.url ?? '',
							altText: 'Primary image'
						}
					: null,
				text: link.linkText ?? '',
				href: link.url ?? '',
				target: link.openInNewTab ? '_blank' : ''
			}
		: null;
});

const mediaGallery = computed((): Array<InvestorUpdateMediaItem> | null => {
	if (!props.asset.ventureCapitalFields?.primaryMediaItem) {
		return null;
	}

	return [
		{
			itemOrder: '0',
			thumbnailUrl: primaryItemImage.value.url,
			sourceUrl: primaryItemImage.value.url,
			mediaItemType: 'PHOTO'
		}
	];
});

const aboutAssetSectionTitle = computed((): string => {
	return props.asset.ventureCapitalFields?.aboutAssetSectionTitle ?? '';
});

const aboutAssetSectionContent = computed((): Array<SanityRichTextBlock> => {
	return props.asset.ventureCapitalFields?.aboutAssetSectionContent ?? [];
});
</script>

<style lang="scss">
@use '../../../styles/constants/colors.scss' as *;
@use '../../../styles/utilities/respond-to.scss' as *;
@use 'sass:map';

.asset-details-about-company {
	display: flex;
	flex-direction: column-reverse;
	gap: 16px;
	margin-top: 16px;

	@include respond-to(md) {
		flex-direction: row;
		gap: 48px;
	}

	&__primary-item {
		max-height: 216px;
		object-fit: cover;

		@include respond-to(md) {
			max-height: 272px;
		}
	}

	&__image-with-link {
		position: relative;
	}

	&__overlay-link {
		position: absolute;
		display: flex;
		align-items: center;
		gap: 4px;
		bottom: 16px;
		left: 16px;
		padding: 4px 8px;
		border-radius: 8px;
		background-color: map.get($brand-colors, 'white');
		color: currentColor;
	}

	&__secondary-items {
		display: flex;
		gap: 16px;
		margin-top: 16px;
		flex-direction: column;

		@include respond-to(lg) {
			flex-direction: row;
		}
	}
}
</style>
